import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
import { patternFields } from '../_shared/pattern.js';
import { headingBlockSchema } from '../../blocks/heading/index.js';
import { textBlockSchema } from '../../blocks/text/index.js';
import { imageBlockSchema } from '../../blocks/image/index.js';
import { buttonBlockSchema } from '../../blocks/button/index.js';
import { dividerBlockSchema } from '../../blocks/divider/index.js';
import { iconWithContentBlockSchema } from '../../blocks/icon-with-content/index.js';
import { iconWithTextBlockSchema } from '../../blocks/icon-with-text/index.js';
/**
 * timeline — Shrine "Timeline" (`sections/goat-timeline.liquid` + its `blocks/timeline*.liquid`), ported
 * config-by-config. A vertical/horizontal progression of "items", each with a POINT (icon / custom image /
 * dot / text) on a connecting LINE, a HEADER + BODY, and an optional "Active content" that swaps into a
 * side/outlet container in the scroll/click/accordion modes. The two top-level blocks mirror Shrine exactly:
 *  - `timeline-content` — the heading / description side (positioned left / right / top / bottom / hidden).
 *  - `timeline`         — the actual list of `timeline-item`s, each holding a `timeline-item_header`,
 *                         `timeline-item_content`, and `timeline-item_active-content`.
 *
 * The section owns every visual knob (line colour/thickness/spacing, point size/bg/border/icon, inactive
 * styling, animation durations, direction/style, layout + sticky content, colour scheme, background pattern,
 * page width, padding). Because the structural chrome (point/line/rail, layout, sticky) is entirely driven by
 * SECTION settings, the section Component renders the whole tree itself and delegates only the leaf content
 * slots to the recursive block renderer — so the structural block schemas below live here (for the builder's
 * add-menu / inspector / preset instantiation) rather than in the global block registry.
 */
/** The generic content blocks a timeline slot / content side accepts (the subset shipped as registered blocks). */
const CONTENT_ACCEPTS = [
    'heading',
    'text',
    'image',
    'button',
    'divider',
    'icon-with-content',
    'icon-with-text',
];
// ── The 7 structural block schemas (Shrine `blocks/timeline*.liquid`). ────────────────────────────────
/** Shrine `timeline-content` — the heading / description side (holds generic blocks + the active-content outlet). */
const timelineContentBlockSchema = {
    type: 'timeline-content',
    name: 'Content',
    // One content side per section (Shrine's two top blocks = 1 content + 1 timeline).
    max: 1,
    accepts: [...CONTENT_ACCEPTS, 'timeline_active-content-container'],
    fields: [],
};
/** Shrine `timeline_active-content-container` — the outlet the active item's "Active content" is swapped into. */
const timelineActiveContentContainerBlockSchema = {
    type: 'timeline_active-content-container',
    name: 'Active content container',
    max: 1,
    fields: [
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'desktop-hidden', label: 'Mobile only' },
                { value: 'mobile-hidden', label: 'Desktop only' },
            ],
        },
    ],
};
/** Shrine `timeline-item_header` — the always-visible header row (aligned to the point). One per item. */
const timelineItemHeaderBlockSchema = {
    type: 'timeline-item_header',
    name: 'Header',
    max: 1,
    accepts: [...CONTENT_ACCEPTS],
    fields: [],
};
/** Shrine `timeline-item_content` — the item body (shown/expanded per style). One per item. */
const timelineItemContentBlockSchema = {
    type: 'timeline-item_content',
    name: 'Content',
    max: 1,
    accepts: [...CONTENT_ACCEPTS],
    fields: [],
};
/** Shrine `timeline-item_active-content` — content surfaced in the outlet when the item is active. One per item. */
const timelineItemActiveContentBlockSchema = {
    type: 'timeline-item_active-content',
    name: 'Active content',
    max: 1,
    accepts: [...CONTENT_ACCEPTS],
    fields: [],
};
/** Shrine `timeline-item` — one point on the timeline; owns the POINT content (icon / image / dot / text). */
const timelineItemBlockSchema = {
    type: 'timeline-item',
    name: 'Timeline item',
    accepts: ['timeline-item_header', 'timeline-item_content', 'timeline-item_active-content'],
    fields: [
        {
            id: 'pointContent',
            type: 'select',
            label: 'Point content',
            group: 'Point',
            default: 'icon',
            options: [
                { value: 'icon', label: 'Icon' },
                { value: 'custom_icon', label: 'Custom icon' },
                { value: 'dot', label: 'Dot' },
                { value: 'text', label: 'Text' },
            ],
        },
        {
            id: 'pointIconName',
            type: 'icon-name',
            label: 'Icon',
            group: 'Point',
            default: 'check_circle',
            info: 'Search any Material Symbols glyph name on fonts.google.com/icons and paste it here.',
            visibleWhen: { field: 'pointContent', equals: 'icon' },
        },
        {
            id: 'pointIconFilled',
            type: 'checkbox',
            label: 'Filled icon',
            group: 'Point',
            default: false,
            visibleWhen: { field: 'pointContent', equals: 'icon' },
        },
        {
            id: 'pointCustomIcon',
            type: 'image',
            label: 'Custom icon',
            group: 'Point',
            visibleWhen: { field: 'pointContent', equals: 'custom_icon' },
        },
        {
            id: 'pointText',
            type: 'text',
            label: 'Point text',
            group: 'Point',
            default: '1',
            info: 'Keep to 1–3 characters.',
            visibleWhen: { field: 'pointContent', equals: 'text' },
        },
    ],
};
/** Shrine `timeline` — the `<ol>` of `timeline-item`s. One per section. */
const timelineListBlockSchema = {
    type: 'timeline',
    name: 'Timeline',
    max: 1,
    accepts: ['timeline-item'],
    fields: [],
};
// ── Section field options (Shrine `goat-timeline.liquid` schema). ─────────────────────────────────────
const COLOR_TYPE_FULL = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'text', label: 'Text' },
    { value: 'color', label: 'Custom' },
    { value: 'gradient', label: 'Gradient' },
];
const COLOR_TYPE_NO_GRADIENT = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'text', label: 'Text' },
    { value: 'color', label: 'Custom' },
];
const DEFAULT_GRADIENT = 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)';
const sectionFields = [
    {
        id: 'displayId',
        type: 'checkbox',
        label: 'Display section ID',
        info: 'Adds an anchor id to this section so a button link can scroll to it.',
        default: false,
    },
    {
        id: 'visibility',
        type: 'select',
        label: 'Display on',
        default: 'always-display',
        options: [
            { value: 'desktop-hidden', label: 'Mobile only' },
            { value: 'mobile-hidden', label: 'Desktop only' },
            { value: 'always-display', label: 'All devices' },
        ],
    },
    // ── General ────────────────────────────────────────────────────────────────────────────────────────
    {
        id: 'desktopPageWidth',
        type: 'select',
        label: 'Page width',
        group: 'General',
        default: 'normal',
        options: [
            { value: 'page-width--narrow', label: 'Narrow' },
            { value: 'normal', label: 'Normal' },
            { value: 'desktop-full-page', label: 'Full width' },
            { value: 'desktop-full-page-no-padding', label: 'Full width without side spacing' },
            { value: 'custom', label: 'Custom' },
        ],
    },
    {
        id: 'customPageWidth',
        type: 'number',
        label: 'Custom page width',
        group: 'General',
        default: 900,
        unit: 'px',
        visibleWhen: { field: 'desktopPageWidth', equals: 'custom' },
    },
    {
        id: 'colorScheme',
        type: 'select',
        label: 'Color scheme',
        group: 'General',
        default: 'background-1',
        options: [
            { value: 'accent-1', label: 'Accent 1' },
            { value: 'accent-2', label: 'Accent 2' },
            { value: 'background-1', label: 'Background 1' },
            { value: 'background-2', label: 'Background 2' },
            { value: 'inverse', label: 'Inverse' },
            { value: 'custom', label: 'Custom' },
        ],
    },
    {
        id: 'customColorsBackground',
        type: 'color',
        label: 'Custom background color',
        group: 'General',
        default: { ref: 'custom', value: '#FFFFFF' },
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customGradientBackground',
        type: 'gradient',
        label: 'Custom gradient background',
        group: 'General',
        info: 'If added, replaces the background color when applicable.',
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customImageBackground',
        type: 'image',
        label: 'Custom image background',
        group: 'General',
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customMobileImageBackground',
        type: 'image',
        label: 'Custom mobile image background',
        group: 'General',
        info: 'If empty, the custom image background is also used on mobile.',
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customColorsText',
        type: 'color',
        label: 'Custom text color',
        group: 'General',
        default: { ref: 'custom', value: '#2E2A39' },
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    // NOTE: the timeline renders NO own button markup — its buttons are nested `button` blocks that carry
    // their OWN custom-colour controls. The Shrine-copied section-level `customColorsSolidButton*` /
    // `customColorsOutlineButton` controls could only be "wired" by rebinding the shared `--aqx-scheme-*`
    // vars, which also drive nested heading/text/icon colours (side effects), so they were removed rather
    // than left as dead controls (a nested button block's own custom-colour fields cover the intent).
    // ── Background pattern (shared Shrine engine). ───────────────────────────────────────────────────────
    ...patternFields(),
    // ── Desktop layout ──────────────────────────────────────────────────────────────────────────────────
    {
        id: 'contentPosition',
        type: 'select',
        label: 'Content position',
        group: 'Desktop layout',
        default: 'left',
        options: [
            { value: 'top', label: 'Top' },
            { value: 'right', label: 'Right' },
            { value: 'bottom', label: 'Bottom' },
            { value: 'left', label: 'Left' },
            { value: 'hidden', label: 'Hidden' },
        ],
    },
    {
        id: 'stickyContent',
        type: 'checkbox',
        label: 'Sticky content',
        group: 'Desktop layout',
        default: true,
        info: "Makes the 'Content' block stick to the top of the viewport as you scroll past the timeline.",
        visibleWhen: { field: 'contentPosition', equals: ['left', 'right'] },
    },
    {
        id: 'contentVerticalAlignment',
        type: 'select',
        label: 'Content vertical alignment',
        group: 'Desktop layout',
        default: 'flex-start',
        options: [
            { value: 'flex-start', label: 'Top' },
            { value: 'center', label: 'Middle' },
            { value: 'flex-end', label: 'Bottom' },
        ],
        visibleWhen: { field: 'stickyContent', equals: false },
    },
    {
        id: 'spacingBetweenBlocksDesktop',
        type: 'range',
        label: 'Spacing',
        group: 'Desktop layout',
        min: 0,
        max: 100,
        step: 5,
        unit: 'px',
        default: 45,
        info: 'Spacing between the timeline & content',
        visibleWhen: { field: 'contentPosition', notEquals: 'hidden' },
    },
    {
        id: 'timelineWidthSide',
        type: 'range',
        label: 'Timeline width',
        group: 'Desktop layout',
        min: 30,
        max: 70,
        step: 5,
        unit: '%',
        default: 50,
        visibleWhen: { field: 'contentPosition', equals: ['left', 'right'] },
    },
    {
        id: 'timelineWidthStacked',
        type: 'range',
        label: 'Timeline width',
        group: 'Desktop layout',
        min: 50,
        max: 100,
        step: 5,
        unit: '%',
        default: 100,
        visibleWhen: { field: 'contentPosition', equals: ['top', 'bottom'] },
    },
    {
        id: 'timelineAlignmentStacked',
        type: 'select',
        label: 'Timeline alignment',
        group: 'Desktop layout',
        default: 'center',
        options: [
            { value: 'flex-start', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'flex-end', label: 'Right' },
        ],
        visibleWhen: { field: 'contentPosition', equals: ['top', 'bottom'] },
    },
    {
        id: 'contentWidthStacked',
        type: 'range',
        label: 'Content width',
        group: 'Desktop layout',
        min: 50,
        max: 100,
        step: 5,
        unit: '%',
        default: 100,
        visibleWhen: { field: 'contentPosition', equals: ['top', 'bottom'] },
    },
    {
        id: 'contentAlignmentStacked',
        type: 'select',
        label: 'Content alignment',
        group: 'Desktop layout',
        default: 'center',
        options: [
            { value: 'flex-start', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'flex-end', label: 'Right' },
        ],
        visibleWhen: { field: 'contentPosition', equals: ['top', 'bottom'] },
    },
    // ── Mobile layout ───────────────────────────────────────────────────────────────────────────────────
    {
        id: 'contentPositionMobile',
        type: 'select',
        label: 'Content position',
        group: 'Mobile layout',
        default: 'top',
        options: [
            { value: 'top', label: 'Top' },
            { value: 'bottom', label: 'Bottom' },
            { value: 'hidden', label: 'Hidden' },
        ],
    },
    {
        id: 'spacingBetweenBlocksMobile',
        type: 'range',
        label: 'Spacing',
        group: 'Mobile layout',
        min: 0,
        max: 50,
        step: 2,
        unit: 'px',
        default: 24,
        info: 'Spacing between the timeline & content',
        visibleWhen: { field: 'contentPositionMobile', notEquals: 'hidden' },
    },
    {
        id: 'timelineWidthMobile',
        type: 'range',
        label: 'Timeline width',
        group: 'Mobile layout',
        min: 50,
        max: 100,
        step: 5,
        unit: '%',
        default: 100,
    },
    {
        id: 'timelineAlignmentMobile',
        type: 'select',
        label: 'Timeline alignment',
        group: 'Mobile layout',
        default: 'center',
        options: [
            { value: 'flex-start', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'flex-end', label: 'Right' },
        ],
    },
    {
        id: 'contentWidthMobile',
        type: 'range',
        label: 'Content width',
        group: 'Mobile layout',
        min: 50,
        max: 100,
        step: 5,
        unit: '%',
        default: 100,
        visibleWhen: { field: 'contentPositionMobile', notEquals: 'hidden' },
    },
    {
        id: 'contentAlignmentMobile',
        type: 'select',
        label: 'Content alignment',
        group: 'Mobile layout',
        default: 'center',
        options: [
            { value: 'flex-start', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'flex-end', label: 'Right' },
        ],
        visibleWhen: { field: 'contentPositionMobile', notEquals: 'hidden' },
    },
    // ── Timeline settings ───────────────────────────────────────────────────────────────────────────────
    {
        id: 'timelineDirection',
        type: 'select',
        label: 'Timeline direction',
        group: 'Timeline settings',
        default: 'vertical',
        options: [
            { value: 'vertical', label: 'Vertical' },
            { value: 'horizontal', label: 'Horizontal' },
        ],
    },
    {
        id: 'styleVertical',
        type: 'select',
        label: 'Style',
        group: 'Timeline settings',
        default: 'classic',
        options: [
            { value: 'classic', label: 'Classic' },
            { value: 'scroll_activated', label: 'Scroll / click activated' },
            { value: 'accordion', label: 'Accordion' },
        ],
        visibleWhen: { field: 'timelineDirection', equals: 'vertical' },
    },
    {
        id: 'styleHorizontal',
        type: 'select',
        label: 'Style',
        group: 'Timeline settings',
        default: 'classic',
        options: [
            { value: 'classic', label: 'Classic' },
            { value: 'slider_click', label: 'Arrows / click activated' },
        ],
        visibleWhen: { field: 'timelineDirection', equals: 'horizontal' },
    },
    {
        id: 'verticalLinePositionDesktop',
        type: 'select',
        label: 'Desktop line position',
        group: 'Timeline settings',
        default: 'left',
        options: [
            { value: 'left', label: 'Left' },
            { value: 'middle', label: 'Middle (alternating items)' },
            { value: 'right', label: 'Right' },
        ],
        visibleWhen: { field: 'timelineDirection', equals: 'vertical' },
    },
    {
        id: 'verticalLinePositionMobile',
        type: 'select',
        label: 'Mobile line position',
        group: 'Timeline settings',
        default: 'left',
        options: [
            { value: 'left', label: 'Left' },
            { value: 'right', label: 'Right' },
        ],
        visibleWhen: { field: 'timelineDirection', equals: 'vertical' },
    },
    {
        id: 'verticalItemsSpacing',
        type: 'range',
        label: 'Spacing between items',
        group: 'Timeline settings',
        min: 0,
        max: 60,
        step: 3,
        unit: 'px',
        default: 15,
        visibleWhen: { field: 'timelineDirection', equals: 'vertical' },
    },
    {
        id: 'horizontalItemPadding',
        type: 'range',
        label: 'Desktop items padding',
        group: 'Timeline settings',
        min: 0,
        max: 60,
        step: 3,
        unit: 'px',
        default: 21,
        visibleWhen: { field: 'timelineDirection', equals: 'horizontal' },
    },
    {
        id: 'horizontalItemPaddingMobile',
        type: 'range',
        label: 'Mobile items padding',
        group: 'Timeline settings',
        min: 0,
        max: 60,
        step: 3,
        unit: 'px',
        default: 30,
        visibleWhen: { field: 'timelineDirection', equals: 'horizontal' },
    },
    {
        id: 'headerBodySpacing',
        type: 'range',
        label: 'Items header & body spacing',
        group: 'Timeline settings',
        min: 0,
        max: 40,
        step: 2,
        unit: 'px',
        default: 10,
    },
    {
        id: 'autoAlign',
        type: 'checkbox',
        label: 'Auto align Heading & Text blocks',
        group: 'Timeline settings',
        default: true,
        info: 'If enabled, Heading & Text blocks are aligned towards the line and their own alignment settings are ignored.',
    },
    // ── Slider & arrows (horizontal). ─────────────────────────────────────────────────────────────────────
    // The arrow controls only function in the horizontal `slider_click` style (the engine wires prev/next
    // there), so they are gated to it — the `visibleWhen` infra keys off a SINGLE sibling field, and
    // `slider_click` is only reachable while direction is horizontal, so this also implies horizontal.
    {
        id: 'arrowsColorScheme',
        type: 'select',
        label: 'Arrows color scheme',
        group: 'Slider & arrows',
        default: 'inverse',
        options: [
            { value: 'accent-1', label: 'Accent 1' },
            { value: 'accent-2', label: 'Accent 2' },
            { value: 'background-1', label: 'Background 1' },
            { value: 'background-2', label: 'Background 2' },
            { value: 'inverse', label: 'Inverse' },
        ],
        visibleWhen: { field: 'styleHorizontal', equals: 'slider_click' },
    },
    {
        id: 'transparentArrows',
        type: 'checkbox',
        label: 'Transparent arrows',
        group: 'Slider & arrows',
        default: false,
        visibleWhen: { field: 'styleHorizontal', equals: 'slider_click' },
    },
    {
        id: 'desktopArrowsPosition',
        type: 'select',
        label: 'Desktop arrows position',
        group: 'Slider & arrows',
        default: 'sides',
        options: [
            { value: 'hidden', label: 'Hidden' },
            { value: 'above', label: 'Above the timeline' },
            { value: 'sides', label: 'On sides of the timeline' },
            { value: 'under', label: 'Under the timeline' },
        ],
        visibleWhen: { field: 'styleHorizontal', equals: 'slider_click' },
    },
    {
        id: 'desktopMaxHorizontalPoints',
        type: 'range',
        label: 'Maximum number of items to show on desktop',
        group: 'Slider & arrows',
        min: 1,
        max: 10,
        step: 1,
        default: 2,
        info: 'If more items are added than this, a slider is also displayed on desktop.',
        visibleWhen: { field: 'timelineDirection', equals: 'horizontal' },
    },
    {
        id: 'mobileArrowsPosition',
        type: 'select',
        label: 'Mobile arrows position',
        group: 'Slider & arrows',
        default: 'under',
        options: [
            { value: 'hidden', label: 'Hidden' },
            { value: 'above', label: 'Above the timeline' },
            { value: 'sides', label: 'On sides of the timeline' },
            { value: 'under', label: 'Under the timeline' },
        ],
        visibleWhen: { field: 'styleHorizontal', equals: 'slider_click' },
    },
    // ── Timeline line ───────────────────────────────────────────────────────────────────────────────────
    {
        id: 'lineThickness',
        type: 'range',
        label: 'Thickness',
        group: 'Timeline line',
        min: 1,
        max: 8,
        step: 1,
        unit: 'px',
        default: 3,
    },
    {
        id: 'lineColorType',
        type: 'select',
        label: 'Color type',
        group: 'Timeline line',
        default: 'accent-1',
        options: COLOR_TYPE_FULL,
    },
    {
        id: 'lineColorSolid',
        type: 'color',
        label: 'Color',
        group: 'Timeline line',
        default: { ref: 'custom', value: '#000000' },
        visibleWhen: { field: 'lineColorType', equals: 'color' },
    },
    {
        id: 'lineColorGradient',
        type: 'gradient',
        label: 'Gradient',
        group: 'Timeline line',
        default: 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)',
        visibleWhen: { field: 'lineColorType', equals: 'gradient' },
    },
    {
        id: 'linePointSpacing',
        type: 'range',
        label: 'Spacing between the line & points',
        group: 'Timeline line',
        min: 0,
        max: 30,
        step: 2,
        unit: 'px',
        default: 4,
        visibleWhen: { field: 'timelineDirection', equals: 'vertical' },
    },
    {
        id: 'linePointSpacingHorizontal',
        type: 'range',
        label: 'Spacing between the line & points',
        group: 'Timeline line',
        min: 0,
        max: 30,
        step: 2,
        unit: 'px',
        default: 8,
        visibleWhen: { field: 'timelineDirection', equals: 'horizontal' },
    },
    // ── Timeline points ─────────────────────────────────────────────────────────────────────────────────
    {
        id: 'pointSizeDesktop',
        type: 'range',
        label: 'Desktop size',
        group: 'Timeline points',
        min: 20,
        max: 80,
        step: 2,
        unit: 'px',
        default: 48,
        visibleWhen: { field: 'timelineDirection', equals: 'vertical' },
    },
    {
        id: 'pointSizeMobile',
        type: 'range',
        label: 'Mobile size',
        group: 'Timeline points',
        min: 20,
        max: 60,
        step: 2,
        unit: 'px',
        default: 40,
        visibleWhen: { field: 'timelineDirection', equals: 'vertical' },
    },
    {
        id: 'horizontalPointSizeDesktop',
        type: 'range',
        label: 'Desktop size',
        group: 'Timeline points',
        min: 30,
        max: 90,
        step: 2,
        unit: 'px',
        default: 54,
        visibleWhen: { field: 'timelineDirection', equals: 'horizontal' },
    },
    {
        id: 'horizontalPointSizeMobile',
        type: 'range',
        label: 'Mobile size',
        group: 'Timeline points',
        min: 30,
        max: 90,
        step: 2,
        unit: 'px',
        default: 54,
        visibleWhen: { field: 'timelineDirection', equals: 'horizontal' },
    },
    {
        id: 'pointBackgroundType',
        type: 'select',
        label: 'Background color',
        group: 'Timeline points',
        default: 'accent-1',
        options: [{ value: 'none', label: 'Transparent' }, ...COLOR_TYPE_FULL],
    },
    {
        id: 'pointBackgroundSolid',
        type: 'color',
        label: 'Background',
        group: 'Timeline points',
        default: { ref: 'custom', value: '#000000' },
        visibleWhen: { field: 'pointBackgroundType', equals: 'color' },
    },
    {
        id: 'pointBackgroundGradient',
        type: 'gradient',
        label: 'Background',
        group: 'Timeline points',
        default: DEFAULT_GRADIENT,
        visibleWhen: { field: 'pointBackgroundType', equals: 'gradient' },
    },
    {
        id: 'pointBorderThickness',
        type: 'range',
        label: 'Border thickness',
        group: 'Timeline points',
        min: 0,
        max: 8,
        step: 1,
        unit: 'px',
        default: 2,
    },
    {
        id: 'pointBorderColorType',
        type: 'select',
        label: 'Border color type',
        group: 'Timeline points',
        default: 'accent-1',
        options: COLOR_TYPE_NO_GRADIENT,
        visibleWhen: { field: 'pointBorderThickness', notEquals: 0 },
    },
    {
        id: 'pointBorderColorSolid',
        type: 'color',
        label: 'Border color',
        group: 'Timeline points',
        default: { ref: 'custom', value: '#000000' },
        visibleWhen: { field: 'pointBorderColorType', equals: 'color' },
    },
    {
        id: 'pointCornerRadius',
        type: 'range',
        label: 'Corner radius',
        group: 'Timeline points',
        min: 0,
        max: 100,
        step: 5,
        unit: '%',
        default: 100,
    },
    // ── Point icons & text ──────────────────────────────────────────────────────────────────────────────
    {
        id: 'pointIconColorType',
        type: 'select',
        label: 'Color type',
        group: 'Point icons & text',
        default: 'background-1',
        options: COLOR_TYPE_NO_GRADIENT,
    },
    {
        id: 'pointIconColorSolid',
        type: 'color',
        label: 'Custom color',
        group: 'Point icons & text',
        default: { ref: 'custom', value: '#ffffff' },
        visibleWhen: { field: 'pointIconColorType', equals: 'color' },
    },
    {
        id: 'pointIconScale',
        type: 'range',
        label: 'Icons scale',
        group: 'Point icons & text',
        min: 30,
        max: 100,
        step: 5,
        unit: '%',
        default: 60,
        info: 'Relative to the point size.',
    },
    {
        id: 'pointDotScale',
        type: 'range',
        label: 'Dots scale',
        group: 'Point icons & text',
        min: 20,
        max: 80,
        step: 5,
        unit: '%',
        default: 40,
        info: 'Relative to the point size.',
    },
    {
        id: 'pointTextScale',
        type: 'range',
        label: 'Text scale',
        group: 'Point icons & text',
        min: 20,
        max: 80,
        step: 5,
        unit: '%',
        default: 60,
        info: 'Relative to the point size.',
    },
    // ── Inactive items ──────────────────────────────────────────────────────────────────────────────────
    {
        id: 'inactiveItemOpacity',
        type: 'range',
        label: 'Inactive item content opacity',
        group: 'Inactive items',
        min: 0,
        max: 100,
        step: 5,
        unit: '%',
        default: 70,
    },
    {
        id: 'inactiveLineColorType',
        type: 'select',
        label: 'Inactive line color type',
        group: 'Inactive items',
        default: 'color',
        options: [
            { value: 'none', label: 'Transparent' },
            { value: 'color', label: 'Color' },
            { value: 'gradient', label: 'Gradient' },
        ],
    },
    {
        id: 'inactiveLineColorSolid',
        type: 'color',
        label: 'Inactive line color',
        group: 'Inactive items',
        default: { ref: 'custom', value: '#d0d0d0' },
        visibleWhen: { field: 'inactiveLineColorType', equals: 'color' },
    },
    {
        id: 'inactiveLineColorGradient',
        type: 'gradient',
        label: 'Inactive line gradient',
        group: 'Inactive items',
        visibleWhen: { field: 'inactiveLineColorType', equals: 'gradient' },
    },
    {
        id: 'inactivePointBorderColorType',
        type: 'select',
        label: 'Inactive point border color type',
        group: 'Inactive items',
        default: 'color',
        options: [
            { value: 'none', label: 'Transparent' },
            { value: 'color', label: 'Solid color' },
        ],
    },
    {
        id: 'inactivePointBorderColorSolid',
        type: 'color',
        label: 'Inactive point border color',
        group: 'Inactive items',
        default: { ref: 'custom', value: '#d0d0d0' },
        visibleWhen: { field: 'inactivePointBorderColorType', equals: 'color' },
    },
    {
        id: 'inactivePointBackgroundType',
        type: 'select',
        label: 'Inactive point background type',
        group: 'Inactive items',
        default: 'color',
        options: [
            { value: 'none', label: 'Transparent' },
            { value: 'color', label: 'Color' },
            { value: 'gradient', label: 'Gradient' },
        ],
    },
    {
        id: 'inactivePointBackgroundSolid',
        type: 'color',
        label: 'Inactive point background color',
        group: 'Inactive items',
        default: { ref: 'custom', value: '#fafafa' },
        visibleWhen: { field: 'inactivePointBackgroundType', equals: 'color' },
    },
    {
        id: 'inactivePointBackgroundGradient',
        type: 'gradient',
        label: 'Inactive point background gradient',
        group: 'Inactive items',
        visibleWhen: { field: 'inactivePointBackgroundType', equals: 'gradient' },
    },
    {
        id: 'inactivePointIconColorType',
        type: 'select',
        label: 'Inactive point icon & text color type',
        group: 'Inactive items',
        default: 'color',
        options: [
            { value: 'none', label: 'Transparent' },
            { value: 'color', label: 'Custom color' },
        ],
    },
    {
        id: 'inactivePointIconColorSolid',
        type: 'color',
        label: 'Inactive point icon & text color',
        group: 'Inactive items',
        default: { ref: 'custom', value: '#d0d0d0' },
        visibleWhen: { field: 'inactivePointIconColorType', equals: 'color' },
    },
    // ── Animations ──────────────────────────────────────────────────────────────────────────────────────
    {
        id: 'pointAnimationDuration',
        type: 'range',
        label: 'Point animation duration',
        group: 'Animations',
        min: 0.1,
        max: 1,
        step: 0.1,
        unit: 's',
        default: 0.5,
    },
    {
        id: 'lineAnimationDuration',
        type: 'range',
        label: 'Line animation duration',
        group: 'Animations',
        min: 0.1,
        max: 1,
        step: 0.1,
        unit: 's',
        default: 0.4,
    },
    {
        id: 'animateLoadingVertical',
        type: 'checkbox',
        label: 'Animate items on scroll-in',
        group: 'Animations',
        default: true,
        visibleWhen: { field: 'timelineDirection', equals: 'vertical' },
    },
    {
        id: 'animateLoadingHorizontal',
        type: 'checkbox',
        label: 'Animate items on scroll-in',
        group: 'Animations',
        default: true,
        visibleWhen: { field: 'timelineDirection', equals: 'horizontal' },
    },
    // ── Section padding ─────────────────────────────────────────────────────────────────────────────────
    {
        id: 'paddingTop',
        type: 'range',
        label: 'Top padding',
        group: 'Section padding',
        min: 0,
        max: 100,
        step: 4,
        unit: 'px',
        default: 36,
    },
    {
        id: 'paddingBottom',
        type: 'range',
        label: 'Bottom padding',
        group: 'Section padding',
        min: 0,
        max: 100,
        step: 4,
        unit: 'px',
        default: 36,
    },
    // The universal Animation group (2026-09-18): this section writes its own section controls
    // instead of spreading `universalFields`, so the group is spread in on its own.
    ...textColorFields(),
    ...animationFields,
];
export const timelineSchema = {
    type: 'timeline',
    name: 'Timeline',
    version: 1,
    templates: ['index', 'collection', 'page', 'product', 'thank-you'],
    // The section takes the two Shrine top blocks; every nested descendant schema is listed for the recursive
    // instantiator + the builder's add-menu / inspector (the section renders their structure itself).
    accepts: ['timeline-content', 'timeline'],
    blocks: [
        timelineContentBlockSchema,
        timelineActiveContentContainerBlockSchema,
        timelineListBlockSchema,
        timelineItemBlockSchema,
        timelineItemHeaderBlockSchema,
        timelineItemContentBlockSchema,
        timelineItemActiveContentBlockSchema,
        headingBlockSchema,
        textBlockSchema,
        imageBlockSchema,
        buttonBlockSchema,
        dividerBlockSchema,
        iconWithContentBlockSchema,
        iconWithTextBlockSchema,
    ],
    // NOTE: the builder enforces `maxBlocks` as a DEEP count of the WHOLE block tree (builderStore
    // `countBlocksDeep`), not top-level children. The default preset alone is ~40 nested blocks, so a small cap
    // (Shrine's `max_blocks: 2`, which meant "2 TOP-LEVEL blocks") would saturate instantly and suppress every
    // "Add block" button. The "one content + one timeline" top-level intent is enforced per-type via `max: 1` on
    // the two top block schemas instead; this is just a generous runaway-safety cap (~30 richly-filled steps).
    maxBlocks: 300,
    // The default point icon is a fixed chrome glyph unless the merchant types one (an `icon-name` field, which
    // collectIconNames also picks up) — ship it in the subset so it never renders as tofu.
    staticIcons: ['check_circle'],
    fields: sectionFields,
};
/**
 * One Shrine timeline item (WEEK n) — header (label + heading), content, active content. With `withImage`,
 * an `image` block leads the Content slot (empty → placeholder the seller replaces) so a step reads as a
 * photo + copy card — the "Timeline with images" preset.
 */
function itemPreset(week, opts = {}) {
    const contentBlocks = [];
    if (opts.withImage) {
        contentBlocks.push({
            type: 'image',
            settings: { borderRadius: 12, marginTop: 0, marginBottom: 12 },
        });
    }
    contentBlocks.push({
        type: 'text',
        settings: {
            text: {
                format: 'html',
                html: '<p>Use this stage to outline the key goals or tasks for this step. Add details that help clarify what happens during this part of the process.</p>',
            },
            mobileAlignment: 'center',
        },
    }, {
        type: 'text',
        settings: {
            text: {
                format: 'html',
                html: '<p>Include any useful details that help guide the visitor forward.</p>',
            },
            mobileAlignment: 'center',
        },
    });
    return {
        type: 'timeline-item',
        blocks: [
            {
                type: 'timeline-item_header',
                blocks: [
                    {
                        type: 'text',
                        settings: {
                            text: { format: 'html', html: `<p>${week}</p>` },
                            marginBottom: 0,
                            mobileAlignment: 'center',
                        },
                    },
                    {
                        type: 'heading',
                        settings: {
                            title: { format: 'html', html: 'Point heading' },
                            headingSize: 'h3',
                            marginTop: 0,
                        },
                    },
                ],
            },
            { type: 'timeline-item_content', blocks: contentBlocks },
            {
                type: 'timeline-item_active-content',
                blocks: [
                    {
                        type: 'text',
                        settings: {
                            text: { format: 'html', html: `<p>${week} active content</p>` },
                            mobileAlignment: 'center',
                        },
                    },
                ],
            },
        ],
    };
}
/** The `timeline-content` heading side (shared by both presets). */
function contentSidePreset() {
    return {
        type: 'timeline-content',
        blocks: [
            {
                type: 'heading',
                settings: { title: { format: 'html', html: 'Timeline' }, headingSize: 'h1' },
            },
            { type: 'timeline_active-content-container' },
        ],
    };
}
const WEEKS = ['WEEK 1', 'WEEK 2', 'WEEK 3', 'WEEK 4'];
/**
 * Presets: the exact Shrine `goat-timeline` default (a "Timeline" heading side + four WEEK items), plus a
 * "Timeline with images" variant that seeds a photo per step (empty → the seller uploads their own).
 */
export const timelinePresets = [
    {
        name: 'Timeline',
        settings: { colorScheme: 'background-1', paddingTop: 36, paddingBottom: 36 },
        blocks: [contentSidePreset(), { type: 'timeline', blocks: WEEKS.map((w) => itemPreset(w)) }],
    },
    {
        name: 'Timeline with images',
        settings: { colorScheme: 'background-1', paddingTop: 36, paddingBottom: 36 },
        blocks: [
            contentSidePreset(),
            { type: 'timeline', blocks: WEEKS.map((w) => itemPreset(w, { withImage: true })) },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const timelineMeta = {
    label: 'Timeline',
    group: 'Content',
    icon: 'clock',
};
//# sourceMappingURL=schema.js.map